/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { Trash2 } from 'lucide-react';
import { useState } from 'react';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

/* Import libraries */
import {
    clearApplicationErrorLog,
    getApplicationErrorLog,
    type ApplicationErrorLogEntry,
} from '@/lib/app/errorLog';
import { formatImportedAt } from '@/lib/formatting/dateTime';

function ErrorLogTab() {
    const { locale, t } = useI18n();
    const [entries, setEntries] = useState<ApplicationErrorLogEntry[]>(() => (
        [...getApplicationErrorLog()].reverse()
    ));

    const handleClear = () => {
        clearApplicationErrorLog();
        setEntries([]);
    };

    return (
        <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-2">
                <h2>{t('Error Log')}</h2>
                <p>{t('Stores application errors locally to support troubleshooting without browser console output.')}</p>
            </section>

            {entries.length > 0 ? (
                <div className="overflow-x-auto rounded-box border border-base-content/20">
                    <table className="table table-sm w-full">
                        <thead className="bg-base-content/10 text-sm text-base-content/80">
                            <tr>
                                <th className="w-16 px-4 py-3">#</th>
                                <th className="w-52 px-4 py-3">{t('Occurred at')}</th>
                                <th className="px-4 py-3">{t('Error')}</th>
                                <th className="w-72 px-4 py-3">{t('Source')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-base-300">
                            {entries.map((entry, index) => (
                                <tr key={entry.id} className="even:bg-base-content/5 hover:bg-base-content/10">
                                    <td className="align-top px-4 py-3 text-base-content/70">{index + 1}</td>
                                    <td className="align-top px-4 py-3 whitespace-nowrap">
                                        {formatImportedAt(entry.occurredAt, locale, t('Unknown'))}
                                    </td>
                                    <td className="min-w-80 align-top px-4 py-3 break-words">{entry.message}</td>
                                    <td className="align-top px-4 py-3">
                                        <code className="break-all rounded bg-base-200 px-1.5 py-0.5 font-mono text-sm">
                                            {entry.source}
                                        </code>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="dashboard-panel text-base-content/70 p-5">
                    {t('No errors have been logged yet.')}
                </div>
            )}

            <div>
                <button type="button" className="btn btn-outline" onClick={handleClear} disabled={entries.length === 0}>
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                    <span>{t('Clear error log')}</span>
                </button>
            </div>
        </div>
    );
}

export default ErrorLogTab;
